import React, { useEffect, useRef, useState } from 'react'
import { useQuery } from 'urql'
import {
  GET_RECONCILIATION_COMPANIES,
  GET_RECONCILIATION_MATCH_PROGRESS,
  GET_RECONCILIATION_TOP_AUTOMATIC,
} from '../../graphql/reconciliation'

// ─── Categorías fijas de conciliación ────────────────────────────────────────
// Cada barra del gráfico representa MOVIMIENTOS BANCARIOS, no matches individuales.
// Un movimiento se clasifica en una de estas 4 categorías según su campo `remaining`
// y los tipos de match que tiene:
//
//   automatico    → remaining=0 y todos los matches son automáticos
//   mixto         → remaining=0 y tiene matches manuales Y automáticos
//   manual        → remaining=0 y todos los matches son manuales
//   no_conciliado → remaining != 0 (no está 100% conciliado)

const CATEGORY_CONFIG = {
  automatico:    { color: '#488E9D', label: 'Automático' },
  mixto:         { color: '#F59E0B', label: 'Mixto' },
  manual:        { color: '#D95555', label: 'Manual' },
  no_conciliado: { color: '#9CA3AF', label: 'No conciliado' },
}

// Orden de apilamiento en las barras: el "peor" estado queda abajo, el "mejor" arriba
const CATEGORY_STACK_ORDER = ['automatico', 'mixto', 'manual', 'no_conciliado']

const getCategoryColor = (category) =>
  CATEGORY_CONFIG[category]?.color ?? '#6B7280'

const getCategoryLabel = (category) =>
  CATEGORY_CONFIG[category]?.label ?? category

const PERIOD_OPTIONS = [
  { value: 'DAY', label: 'Día' },
  { value: 'WEEK', label: 'Semana' },
  { value: 'MONTH', label: 'Mes' },
  { value: 'YEAR', label: 'Año' },
]

const formatPeriodLabel = (periodKey, granularity) => {
  if (!periodKey) return '-'

  if (granularity === 'MONTH') {
    const [year, month] = periodKey.split('-')
    const date = new Date(Number(year), Number(month) - 1, 1)
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat('es-CL', { month: 'short', year: '2-digit' }).format(date)
    }
  }

  if (granularity === 'DAY') {
    const date = new Date(`${periodKey}T12:00:00`)
    if (!Number.isNaN(date.getTime())) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}/${m}/${d}`
    }
  }

  return periodKey
}

// ─── Gráfico principal: Distribución de movimientos por período ───────────────

function ReconciliationChart({ items, matchSources, granularity }) {
  const [tooltipState, setTooltipState] = useState(null)
  const chartCardRef = useRef(null)
  const chartGridRef = useRef(null)

  const updateTooltipPosition = (event, item) => {
    if (!chartCardRef.current || !chartGridRef.current) return
    const cardRect = chartCardRef.current.getBoundingClientRect()
    const barRect = event.currentTarget.getBoundingClientRect()
    const rawLeft = barRect.left - cardRect.left + barRect.width / 2
    const minLeft = 140
    const maxLeft = Math.max(minLeft, cardRect.width - 140)
    const barTop = barRect.top - cardRect.top
    const preferredTop = barTop - 14
    setTooltipState({
      item,
      left: Math.min(Math.max(rawLeft, minLeft), maxLeft),
      top: Math.max(18, preferredTop),
    })
  }

  if (!items.length) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">%</div>
        <div>No hay movimientos para los filtros seleccionados.</div>
      </div>
    )
  }

  // Ordenar categorías con el orden de apilamiento definido
  const sortedMatchSources = [...matchSources].sort(
    (a, b) => CATEGORY_STACK_ORDER.indexOf(a) - CATEGORY_STACK_ORDER.indexOf(b),
  )

  return (
    <div className="reconciliation-chart-card" ref={chartCardRef}>
      <div className="reconciliation-chart-header">
        <div>
          <h3 className="dashboard-card-title">Distribución de movimientos por período</h3>
          <p className="chart-helper-text">
            Cada barra representa el 100% de los movimientos bancarios del período,
            clasificados por su estado de conciliación.
          </p>
        </div>
      </div>

      <div className="reconciliation-legend">
        {sortedMatchSources.map((category) => (
          <div className="legend-item" key={category}>
            <span
              className="legend-color"
              style={{ backgroundColor: getCategoryColor(category) }}
            />
            <span>{getCategoryLabel(category)}</span>
          </div>
        ))}
      </div>

      <div className="stacked-chart-layout">
        <div className="stacked-chart-y-axis">
          {[100, 75, 50, 25, 0].map((tick) => (
            <span
              key={tick}
              style={{
                top: `${100 - tick}%`,
                transform:
                  tick === 100
                    ? 'translateY(0)'
                    : tick === 0
                      ? 'translateY(-100%)'
                      : 'translateY(-50%)',
              }}
            >
              {tick}%
            </span>
          ))}
        </div>

        <div className="stacked-chart-stage">
          <div className="stacked-chart-scroll">
            <div className="stacked-chart-totals">
              {items.map((item) => (
                <div className="stacked-bar-total" key={`${item.periodKey}-total`}>
                  {item.totalMovements}
                </div>
              ))}
            </div>

            <div className="stacked-chart-grid" ref={chartGridRef}>
              {[100, 75, 50, 25, 0].map((tick) => (
                <div
                  className="stacked-chart-grid-line"
                  key={tick}
                  style={{ top: `${100 - tick}%` }}
                />
              ))}

              <div className="stacked-bars-container">
                {items.map((item) => {
                  // Ordenar los segmentos para un apilamiento consistente
                  const orderedSegments = [...item.segments].sort(
                    (a, b) =>
                      CATEGORY_STACK_ORDER.indexOf(a.matchSource) -
                      CATEGORY_STACK_ORDER.indexOf(b.matchSource),
                  )
                  return (
                    <div
                      className="stacked-bar-column"
                      key={item.periodKey}
                      onMouseEnter={(event) => updateTooltipPosition(event, item)}
                      onMouseMove={(event) => updateTooltipPosition(event, item)}
                      onMouseLeave={() => setTooltipState(null)}
                    >
                      <div className="stacked-bar">
                        {orderedSegments.map((segment) => (
                          <div
                            className="stacked-bar-segment"
                            key={`${item.periodKey}-${segment.matchSource}`}
                            style={{
                              height: `${segment.percentage}%`,
                              backgroundColor: getCategoryColor(segment.matchSource),
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="stacked-chart-labels">
              {items.map((item) => (
                <div className="stacked-bar-meta" key={`${item.periodKey}-meta`}>
                  <div className="stacked-bar-label">
                    {formatPeriodLabel(item.periodKey, granularity)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {tooltipState && (
        <div
          className="stacked-bar-tooltip stacked-bar-tooltip-floating"
          style={{ left: `${tooltipState.left}px`, top: `${tooltipState.top}px` }}
        >
          <div className="stacked-bar-tooltip-title">
            {formatPeriodLabel(tooltipState.item.periodKey, granularity)}
          </div>
          <div className="stacked-bar-tooltip-total">
            Total movimientos: {tooltipState.item.totalMovements}
          </div>
          {[...tooltipState.item.segments]
            .sort(
              (a, b) =>
                CATEGORY_STACK_ORDER.indexOf(b.matchSource) -
                CATEGORY_STACK_ORDER.indexOf(a.matchSource),
            )
            .map((segment) => (
              <div className="stacked-bar-tooltip-row" key={segment.matchSource}>
                <span
                  className="legend-color"
                  style={{ backgroundColor: getCategoryColor(segment.matchSource) }}
                />
                <span>{getCategoryLabel(segment.matchSource)}</span>
                <strong>
                  {segment.count} ({segment.percentage}%)
                </strong>
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

// ─── Dashboard principal ──────────────────────────────────────────────────────

export function ReconciliationProgress() {
  const [companyInputText, setCompanyInputText] = useState('')
  const [companySearchQuery, setCompanySearchQuery] = useState('')
  const [selectedCompanyId, setSelectedCompanyId] = useState('')
  const [selectedCompanySnapshot, setSelectedCompanySnapshot] = useState(null)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [granularity, setGranularity] = useState('MONTH')

  const skipNextInputChange = useRef(false)

  useEffect(() => {
    if (selectedCompanyId) return
    const timeoutId = setTimeout(() => setCompanySearchQuery(companyInputText), 250)
    return () => clearTimeout(timeoutId)
  }, [companyInputText, selectedCompanyId])

  const handleSelectCompany = (companyId, company) => {
    const displayText = `${company.name}${company.rut ? ` - ${company.rut}` : ''}`
    skipNextInputChange.current = true
    setCompanyInputText(displayText)
    setCompanySearchQuery('')
    setSelectedCompanyId(companyId)
    setSelectedCompanySnapshot(company)
    setSelectedUserId('')
  }

  const handleClearSelection = () => {
    setCompanyInputText('')
    setCompanySearchQuery('')
    setSelectedCompanyId('')
    setSelectedCompanySnapshot(null)
    setSelectedUserId('')
  }

  const handleCompanyInputChange = (text) => {
    if (skipNextInputChange.current) {
      skipNextInputChange.current = false
      return
    }
    if (selectedCompanyId) {
      setSelectedCompanyId('')
      setSelectedCompanySnapshot(null)
    }
    setCompanyInputText(text)
  }

  const [companiesResult] = useQuery({
    query: GET_RECONCILIATION_COMPANIES,
    variables: { search: companySearchQuery || null },
    pause: !!selectedCompanyId,
  })

  const [progressResult] = useQuery({
    query: GET_RECONCILIATION_MATCH_PROGRESS,
    variables: {
      filter: {
        granularity,
        companyId: selectedCompanyId || null,
        userRelatedId: selectedUserId || null,
      },
    },
    requestPolicy: 'network-only',
  })

  const companies = companiesResult.data?.reconciliationCompanies || []
  const progress = progressResult.data?.reconciliationMatchProgress
  const items = progress?.items || []
  const matchSources = progress?.matchSources || []
  const userRelatedOptions = progress?.userRelatedOptions || []

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Avance Conciliación</h1>
        <p className="page-subtitle">
          Visualiza el avance de la conciliación por período, empresa y usuario.
          Cada movimiento bancario se clasifica como automático, mixto, manual o no conciliado.
        </p>
      </div>

      <div className="content-card reconciliation-content-card">
        <div className="card-body reconciliation-card-body">
          <div className="filters-row">
            <CompanyAutocomplete
              companies={companies}
              inputValue={companyInputText}
              onInputChange={handleCompanyInputChange}
              onClearSelection={handleClearSelection}
              onSelectCompany={handleSelectCompany}
              selectedCompanyId={selectedCompanyId}
            />

            <div className="filter-group">
              <label className="filter-label">Período</label>
              <select
                className="filter-select"
                onChange={(event) => setGranularity(event.target.value)}
                value={granularity}
              >
                {PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Usuario</label>
              <select
                className="filter-select"
                onChange={(event) => setSelectedUserId(event.target.value)}
                value={selectedUserId}
              >
                <option value="">Todos los usuarios</option>
                {userRelatedOptions.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {user.displayName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="reconciliation-summary-row">
            <div className="reconciliation-summary-card">
              <span className="reconciliation-summary-label">Empresa seleccionada</span>
              <strong>{selectedCompanySnapshot?.name || 'Todas las empresas'}</strong>
              <span className="text-muted">{selectedCompanySnapshot?.rut || 'Sin filtro por RUT'}</span>
            </div>

            <div className="reconciliation-summary-card">
              <span className="reconciliation-summary-label">Períodos con actividad</span>
              <strong>{items.length}</strong>
              <span className="text-muted">Según filtros aplicados</span>
            </div>

            <div className="reconciliation-summary-card">
              <span className="reconciliation-summary-label">Total de movimientos</span>
              <strong>{progress?.totalMovements || 0}</strong>
              <span className="text-muted">Suma de todos los períodos</span>
            </div>
          </div>

          {progressResult.fetching ? (
            <div className="loading-spinner">
              <div className="spinner" />
            </div>
          ) : (
            <ReconciliationChart
              granularity={granularity}
              items={items}
              matchSources={matchSources}
            />
          )}
        </div>
      </div>

      <TopAutomaticChart />
    </>
  )
}

// ─── Gráfico "Top conciliación automática último mes" ─────────────────────────

function TopAutomaticChart() {
  const [result] = useQuery({
    query: GET_RECONCILIATION_TOP_AUTOMATIC,
    requestPolicy: 'network-only',
  })

  const data = result.data?.reconciliationTopAutomatic
  const items = data?.items || []
  const periodLabel = data?.periodLabel || ''

  if (result.fetching) {
    return (
      <div className="content-card reconciliation-content-card" style={{ marginTop: '1.5rem' }}>
        <div className="card-body reconciliation-card-body">
          <div className="loading-spinner"><div className="spinner" /></div>
        </div>
      </div>
    )
  }

  // Los 4 segmentos en el mismo orden visual que el gráfico principal
  const TOP_SEGMENTS = [
    { key: 'notReconciledPercentage', moves: 'notReconciledMovements', category: 'no_conciliado' },
    { key: 'manualPercentage',        moves: 'manualMovements',        category: 'manual' },
    { key: 'mixedPercentage',         moves: 'mixedMovements',         category: 'mixto' },
    { key: 'automaticPercentage',     moves: 'automaticMovements',     category: 'automatico' },
  ]

  return (
    <div className="content-card reconciliation-content-card" style={{ marginTop: '1.5rem' }}>
      <div className="card-body reconciliation-card-body">
        <div className="reconciliation-chart-header" style={{ marginBottom: '1.25rem' }}>
          <div>
            <h3 className="dashboard-card-title">Top conciliación automática último mes</h3>
            <p className="dashboard-card-subtitle">
              Clasificación de movimientos bancarios por empresa en {periodLabel}.
              Ordenado de mayor a menor automatización.
            </p>
          </div>
        </div>

        {/* Leyenda */}
        <div className="reconciliation-legend" style={{ marginBottom: '1rem' }}>
          {TOP_SEGMENTS.slice().reverse().map(({ category }) => (
            <div className="legend-item" key={category}>
              <span className="legend-color" style={{ backgroundColor: getCategoryColor(category) }} />
              <span>{getCategoryLabel(category)}</span>
            </div>
          ))}
        </div>

        {items.length === 0 ? (
          <p className="text-muted" style={{ textAlign: 'center', padding: '2rem 0' }}>
            Sin datos para el período anterior.
          </p>
        ) : (
          <div className="top-automatic-list">
            {items.map((item) => (
              <div key={item.companyId} className="top-automatic-row">
                <div className="top-automatic-label" title={item.companyName}>
                  {item.companyName}
                </div>

                <div className="top-automatic-bar-wrapper">
                  <div className="top-automatic-bar">
                    {TOP_SEGMENTS.map(({ key, category }) => {
                      const pct = item[key]
                      if (!pct) return null
                      return (
                        <div
                          key={category}
                          className="top-automatic-bar-segment"
                          style={{ width: `${pct}%`, backgroundColor: getCategoryColor(category) }}
                          title={`${getCategoryLabel(category)}: ${pct}%`}
                        />
                      )
                    })}
                  </div>
                </div>

                <div className="top-automatic-stats">
                  <span style={{ color: getCategoryColor('automatico') }}>
                    {item.automaticPercentage}% auto
                  </span>
                  {item.mixedPercentage > 0 && (
                    <>
                      <span className="top-automatic-stat-sep">·</span>
                      <span style={{ color: getCategoryColor('mixto') }}>
                        {item.mixedPercentage}% mixto
                      </span>
                    </>
                  )}
                  {item.manualPercentage > 0 && (
                    <>
                      <span className="top-automatic-stat-sep">·</span>
                      <span style={{ color: getCategoryColor('manual') }}>
                        {item.manualPercentage}% manual
                      </span>
                    </>
                  )}
                  {item.notReconciledPercentage > 0 && (
                    <>
                      <span className="top-automatic-stat-sep">·</span>
                      <span style={{ color: getCategoryColor('no_conciliado') }}>
                        {item.notReconciledPercentage}% no conc.
                      </span>
                    </>
                  )}
                  <span className="top-automatic-stat-total">
                    ({item.totalMovements.toLocaleString()} mov.)
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ReconciliationProgress

// ─── Autocomplete de empresa ──────────────────────────────────────────────────

function CompanyAutocomplete({
  companies,
  inputValue,
  onInputChange,
  onSelectCompany,
  onClearSelection,
  selectedCompanyId,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target)) setIsOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  return (
    <div className="filter-group" ref={containerRef} style={{ position: 'relative', minWidth: '300px' }}>
      <label className="filter-label">Empresa</label>
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          className="filter-input search-input"
          placeholder="Buscar empresa (por nombre o RUT)"
          value={inputValue}
          onChange={(e) => {
            onInputChange(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
        />
        {selectedCompanyId && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              onClearSelection()
              setIsOpen(false)
            }}
            className="btn btn-link"
            style={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Limpiar empresa seleccionada"
          >
            ×
          </button>
        )}
      </div>

      {isOpen && (
        <div
          className="content-card"
          style={{
            position: 'absolute',
            zIndex: 20,
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: 280,
            overflowY: 'auto',
          }}
        >
          <div className="card-body" style={{ padding: 0 }}>
            <div className="list">
              <div
                className="list-item"
                key="all"
                onMouseDown={(e) => {
                  e.preventDefault()
                  onClearSelection()
                  setIsOpen(false)
                }}
                style={{ cursor: 'pointer', padding: '8px 12px' }}
              >
                Todas las empresas
              </div>
              {companies.map((company) => (
                <div
                  className="list-item"
                  key={company._id}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    onSelectCompany(company._id, company)
                    setIsOpen(false)
                  }}
                  style={{ cursor: 'pointer', padding: '8px 12px' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span>{company.name}</span>
                    <span className="text-muted">{company.rut || company.alias || ''}</span>
                  </div>
                </div>
              ))}
              {!companies.length && (
                <div className="list-item text-muted" style={{ padding: '8px 12px' }}>
                  Sin resultados
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
